import difference from 'lodash/difference';
import sortBy from 'lodash/sortBy';

import { AccessControlData } from '@cast/shared-access-control-data-access';
import {
  FeatureFlagResolved,
  GetSettingsResponse,
  Organization,
  OrganizationType,
} from '@cast/types';

import { resolveActionFlags } from './resolve-action-flags';
import { ABILITY_CONFIG } from '../config';
import { Actions, ConsoleRules } from '../types';

export const ruleBuilder = ({
  subjectId,
  accessData,
  flags,
  organizationMap,
  orgSettings,
}: {
  subjectId: string;
  accessData: AccessControlData[string];
  flags: FeatureFlagResolved[];
  organizationMap: Record<string, Organization>;
  orgSettings?: GetSettingsResponse;
}): ConsoleRules[] => {
  const rules: ConsoleRules[] = [];

  const subjects = Object.keys(
    ABILITY_CONFIG
  ) as (keyof typeof ABILITY_CONFIG)[];
  const organizationIds = Object.keys(accessData.organizations);
  const clusterIds = Object.keys(accessData.clusters);

  subjects.forEach((subject) => {
    const subjectActions = Object.keys(ABILITY_CONFIG[subject]) as Actions[];
    subjectActions.forEach((action) => {
      if (orgSettings?.readOnly && action !== 'view') {
        organizationIds.forEach((organizationId) => {
          const organization = organizationMap[organizationId];
          const mappedOrganizationId =
            organization?.type !== OrganizationType.ENTERPRISE
              ? organizationId
              : undefined;
          const mappedEnterpriseId =
            organization?.type === OrganizationType.ENTERPRISE
              ? organizationId
              : organization?.type === OrganizationType.CHILD
              ? organization?.parentId
              : undefined;
          rules.push({
            action,
            subject,
            inverted: true,
            conditions: {
              subjectId,
              organizationId: mappedOrganizationId,
              enterpriseId: mappedEnterpriseId,
            },
            reason: 'Organization is read-only',
          });
        });
        clusterIds.forEach((clusterId) => {
          rules.push({
            action,
            subject,
            inverted: true,
            conditions: { subjectId, clusterId },
            reason: 'Organization is read-only',
          });
        });
        return;
      }

      const featureFlags = ABILITY_CONFIG[subject][action]?.flags ?? [];
      const isFeatureToggled = Array.isArray(featureFlags)
        ? featureFlags?.length
        : featureFlags?.flags?.length;

      const isEnabled = isFeatureToggled
        ? resolveActionFlags(featureFlags ?? [], flags)
        : true;

      const requiredPermissions = ABILITY_CONFIG[subject][action]?.groups ?? [];

      organizationIds.forEach((organizationId) => {
        const organization = organizationMap[organizationId];
        const mappedOrganizationId =
          organization?.type !== OrganizationType.ENTERPRISE
            ? organizationId
            : undefined;
        const mappedEnterpriseId =
          organization?.type === OrganizationType.ENTERPRISE
            ? organizationId
            : organization?.type === OrganizationType.CHILD
            ? organization?.parentId
            : undefined;

        if (!isEnabled) {
          rules.push({
            action,
            subject,
            inverted: true,
            conditions: {
              subjectId,
              organizationId: mappedOrganizationId,
              enterpriseId: mappedEnterpriseId,
            },
            reason: `Feature ${subject} is not enabled`,
          });
          return;
        }

        const organizationAccess = accessData.organizations[organizationId];
        const organizationLevelAccess = organizationAccess?.hasAccessTo ?? [];

        const hasPermissions = requiredPermissions.length
          ? requiredPermissions.every((permission) =>
              organizationLevelAccess.includes(permission)
            )
          : true;

        // Skip creating rules for child orgs on enterprise-only subjects
        // Child org rules (even with enterpriseId) would override enterprise-only rules
        const isChildOrg = organization?.type === OrganizationType.CHILD;
        const isEnterpriseOnlySubject = subject === 'EnterpriseManagement';

        if (isChildOrg && isEnterpriseOnlySubject) {
          // Enterprise-only subjects should only have rules from enterprise org
          // Skip child org to prevent deny rules from overriding
          return;
        }

        rules.push({
          action,
          subject,
          inverted: !hasPermissions,
          conditions: {
            subjectId,
            organizationId: mappedOrganizationId,
            enterpriseId: mappedEnterpriseId,
          },
          reason: !hasPermissions
            ? `Missing permissions for ${difference(
                requiredPermissions,
                organizationLevelAccess
              ).join(', ')}`
            : undefined,
        });
      });

      // For flag-only subjects with no required permissions,
      // create a subject-scoped rule when the user has no org-level access.
      // This ensures pure feature toggles apply regardless of access scope.
      if (isEnabled && !requiredPermissions.length && !organizationIds.length) {
        rules.push({
          action,
          subject,
          inverted: false,
          conditions: { subjectId },
        });
      }

      if (isEnabled) {
        clusterIds.forEach((clusterId) => {
          const clusterAccess = accessData.clusters[clusterId];
          const clusterLevelAccess = clusterAccess.hasAccessTo ?? [];

          const hasPermissions = requiredPermissions.length
            ? requiredPermissions.every((permission) =>
                clusterLevelAccess.includes(permission)
              )
            : true;

          rules.push({
            action,
            subject,
            inverted: !hasPermissions,
            conditions: {
              subjectId,
              clusterId,
            },
            reason: !hasPermissions
              ? `Missing permissions for ${difference(
                  requiredPermissions,
                  clusterLevelAccess
                ).join(', ')}`
              : undefined,
          });
        });
      }
    });
  });

  return sortBy(rules, (rule) => {
    const conditions = rule.conditions as {
      clusterId?: string;
      organizationId?: string;
      enterpriseId?: string;
    };
    if (conditions.clusterId) return 2;
    if (conditions.organizationId) return 1;
    if (conditions.enterpriseId) return 0;
    return 3;
  });
};
